/**
 * Session, account and workspace state for the shared React application
 * (prompt.md §6.2, §52, §71).
 *
 * WHY account switching is modelled as a *destructive* transition: a person may
 * hold a personal identity and one or more workplace identities, and their data
 * is isolated per plane. Switching therefore has to clear every cached
 * workspace-scoped value before the new context renders — a cache that survives
 * the switch is a cross-plane data leak, not a performance win.
 *
 * The invalidation rules are pure functions so they can be asserted without a
 * DOM or a server.
 */

import type { WorkspaceId } from "@dayvise/domain";

export type IdentityPlane = "personal" | "work";

export interface SessionAccount {
  readonly identityId: string;
  readonly label: string;
  readonly plane: IdentityPlane;
  readonly email: string | null;
  readonly userId: string;
}

export interface SessionWorkspace {
  readonly workspaceId: WorkspaceId;
  readonly name: string;
  readonly kind: "personal" | "team";
  readonly plan: string;
  readonly planStatus?: "effective" | "cached";
  readonly role: "owner" | "admin" | "member" | "viewer" | "billing";
}

export interface SessionState {
  readonly account: SessionAccount | null;
  readonly workspace: SessionWorkspace | null;
  readonly workspaces: readonly SessionWorkspace[];
  readonly accounts: readonly SessionAccount[];
  readonly status: "signed_out" | "signed_in";
}

export const SIGNED_OUT: SessionState = {
  account: null,
  workspace: null,
  workspaces: [],
  accounts: [],
  status: "signed_out",
};

/**
 * Where the session token is kept.
 *
 * Injected because the three clients store it differently — an Android Keystore
 * entry, a Tauri OS keychain entry, or a browser cookie-backed store — and none
 * of those belong in shared application code.
 */
export interface TokenStore {
  read(): Promise<string | null>;
  write(token: string): Promise<void>;
  clear(): Promise<void>;
}

/** In-memory store: the default for tests and for the browser's session scope. */
export class MemoryTokenStore implements TokenStore {
  private token: string | null = null;

  constructor(initial: string | null = null) {
    this.token = initial;
  }

  async read(): Promise<string | null> {
    return this.token;
  }

  async write(token: string): Promise<void> {
    this.token = token;
  }

  async clear(): Promise<void> {
    this.token = null;
  }
}

/**
 * The cache keyspaces that must be dropped when the identity context changes.
 *
 * Crossing planes drops everything workspace-scoped; staying inside one plane
 * drops only what the switch itself invalidates. Returning the list instead of
 * performing the clearing keeps this testable and lets each client clear its own
 * storage (in-memory caches, IndexedDB, Room) in its own way.
 */
export const PLANE_SCOPED_KEYSPACES: readonly string[] = [
  "conversations",
  "messages",
  "runs",
  "runEvents",
  "memories",
  "files",
  "schedules",
  "credentials",
  "usage",
  "entitlements",
  "toolPolicy",
  "storageRoute",
  "notifications",
  "audit",
];

/** Keyspaces that are re-derived on any context change, plane crossing or not. */
export const CONTEXT_SCOPED_KEYSPACES: readonly string[] = [
  "entitlements",
  "toolPolicy",
  "storageRoute",
];

export interface InvalidationPlan {
  readonly keyspaces: readonly string[];
  readonly reason: string;
}

/** Switching to a different account: crossing planes clears everything. */
export function planAccountSwitch(from: SessionAccount | null, to: SessionAccount): InvalidationPlan {
  if (from === null) {
    return { keyspaces: PLANE_SCOPED_KEYSPACES, reason: "sign-in" };
  }
  if (from.identityId === to.identityId) {
    return { keyspaces: CONTEXT_SCOPED_KEYSPACES, reason: "same identity" };
  }
  if (from.plane !== to.plane) {
    return {
      keyspaces: PLANE_SCOPED_KEYSPACES,
      reason: `crossing the ${from.plane}/${to.plane} boundary clears every cached value`,
    };
  }
  return {
    keyspaces: PLANE_SCOPED_KEYSPACES,
    reason: "a different identity shares no cached workspace state",
  };
}

/** Switching workspace inside one identity: only context-scoped data changes. */
export function planWorkspaceSwitch(from: SessionWorkspace | null, to: SessionWorkspace): InvalidationPlan {
  if (from === null || from.workspaceId === to.workspaceId) {
    return { keyspaces: CONTEXT_SCOPED_KEYSPACES, reason: "workspace selected" };
  }
  return {
    keyspaces: PLANE_SCOPED_KEYSPACES,
    reason: "workspace-scoped data belongs to the workspace that produced it",
  };
}

/**
 * Guard used before rendering a signed-in surface: the client must never hold an
 * account without an active workspace, because every request is workspace
 * scoped.
 */
export function assertRenderableSession(state: SessionState): { readonly ok: true } | { readonly ok: false; readonly reason: string } {
  if (state.status !== "signed_in") return { ok: false, reason: "not signed in" };
  if (state.account === null) return { ok: false, reason: "no account selected" };
  if (state.workspace === null) return { ok: false, reason: "no workspace selected" };
  const workspaceBelongsToPlane = state.workspace.kind === (state.account.plane === "work" ? "team" : "personal");
  if (!workspaceBelongsToPlane) {
    return { ok: false, reason: `a ${state.account.plane} identity cannot open a ${state.workspace.kind} workspace` };
  }
  return { ok: true };
}

/** Accounts grouped by plane, so a switcher can separate them visually. */
export function groupAccounts(accounts: readonly SessionAccount[]): { readonly personal: readonly SessionAccount[]; readonly work: readonly SessionAccount[] } {
  return {
    personal: accounts.filter((account) => account.plane === "personal"),
    work: accounts.filter((account) => account.plane === "work"),
  };
}

/** The workspace the app should open for an account, if any. */
export function defaultWorkspaceFor(account: SessionAccount, workspaces: readonly SessionWorkspace[]): SessionWorkspace | null {
  const expectedKind = account.plane === "work" ? "team" : "personal";
  const candidates = workspaces.filter((workspace) => workspace.kind === expectedKind);
  return candidates[0] ?? null;
}

/** True when a workplace workspace is using organization-managed credentials. */
export function requiresOrganizationCredentials(workspace: SessionWorkspace | null): boolean {
  return workspace?.kind === "team";
}
